// Which stage a running turn is in, read from the step it is on — nothing in the pipeline reports one — and how long it has been there.

const STAGES = [
  { label: "Downloading the source", test: (step) => /fetchUnityRepo/i.test(step.text) },
  { label: "Verifying", test: (step) => /verifyEngine|renderSnapshot|playtest|checkPreview|domProbe/i.test(step.text) },
  { label: "Building the snapshot", test: (step) => ["write", "edit", "multiedit", "notebookedit"].includes(step.name) },
  { label: "Reading the source", test: (step) => ["read", "grep", "glob", "ls"].includes(step.name) },
  { label: "Working", test: (step) => step.kind === "tool" },
];

const STALL_MS = 90 * 1000;

function describe(step) {
  return {
    kind: String(step.kind || ""),
    name: String(step.name || "").toLowerCase(),
    text: `${step.name || ""} ${step.summary || ""}`,
  };
}

/**
 * Which stage a running turn is in, and whether it has stopped moving.
 *
 * Read from the step the agent is on rather than announced by it: nothing in the
 * pipeline reports a stage, and the steps are what actually arrives. `stalledMs`
 * is how long that step has been the last one — the only honest answer to "where
 * is it stuck?".
 */
export function currentStage(turn, now = Date.now()) {
  if (!turn || turn.status !== "running") return null;

  const steps = turn.steps || [];
  const last = steps[steps.length - 1];
  if (!last) return null;

  const described = describe(last);
  const stage = STAGES.find((candidate) => candidate.test(described));
  const at = Date.parse(last.at);
  const stalledMs = Number.isNaN(at) ? 0 : Math.max(0, now - at);

  return {
    label: stage ? stage.label : "Thinking",
    detail: String(last.summary || "").slice(0, 120),
    isError: last.isError === true,
    stalledMs,
    isStalled: stalledMs > STALL_MS,
  };
}

const BACKGROUND_KINDS = [
  ["agent", "agent"],
  ["command", "command"],
  ["remote", "remote agent"],
  ["other", "task"],
];

/**
 * "Background: 3 agents · 1 command" for the work a running turn's agent is
 * still waiting on, plus what each task is (admins only; the server leaves the
 * descriptions out otherwise). Null when nothing runs in the background.
 */
export function backgroundSummary(tasks) {
  if (!Array.isArray(tasks) || !tasks.length) return null;
  const known = new Set(BACKGROUND_KINDS.map(([kind]) => kind));
  const parts = BACKGROUND_KINDS.map(([kind, noun]) => {
    const count = tasks.filter((task) => (known.has(task.kind) ? task.kind : "other") === kind).length;
    return count ? `${count} ${noun}${count === 1 ? "" : "s"}` : null;
  }).filter(Boolean);
  return {
    label: `Background: ${parts.join(" · ")}`,
    descriptions: tasks.map((task) => task.description).filter(Boolean),
  };
}

export function formatStalled(ms) {
  const seconds = Math.round(ms / 1000);
  if (seconds < 90) return `${seconds}s`;
  return `${Math.round(seconds / 60)}m`;
}
